import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound, Loader2, CheckCircle2 } from "lucide-react";
import { API_BASE } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { setToken(params.get("token") || ""); }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match");
    if (password.length < 8 || !/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
      return toast.error("8+ chars, letter + digit required");
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/password/reset`, { token, new_password: password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="reset-page">
      <div className="w-full max-w-sm space-y-6">
        <div>
          <h1 className="font-display text-3xl font-bold">Reset password</h1>
          <p className="text-sm text-muted-foreground mt-1">Pick a strong new password. All active sessions will be signed out.</p>
        </div>
        {!done ? (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            {!params.get("token") && (
              <div className="space-y-1.5">
                <Label htmlFor="token">Reset token</Label>
                <Input id="token" required value={token} onChange={(e) => setToken(e.target.value)}
                       data-testid="reset-token" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="pw1">New password</Label>
              <Input id="pw1" type="password" required minLength={8} value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     autoComplete="new-password" data-testid="reset-password" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw2">Confirm</Label>
              <Input id="pw2" type="password" required value={confirm}
                     onChange={(e) => setConfirm(e.target.value)}
                     autoComplete="new-password" data-testid="reset-confirm" />
              <div className="text-[10px] text-muted-foreground">8+ characters, at least one letter and one digit.</div>
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="reset-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Reset password
            </Button>
          </form>
        ) : (
          <div className="p-6 rounded-2xl border border-[hsl(var(--gain))]/40 bg-[hsl(var(--gain))]/5" data-testid="reset-done">
            <CheckCircle2 className="w-8 h-8 text-[hsl(var(--gain))] mb-2" />
            <div className="font-medium">Password updated.</div>
            <div className="text-sm text-muted-foreground mt-1">Redirecting to sign in…</div>
          </div>
        )}
        <p className="text-xs text-muted-foreground text-center">
          <Link to="/login" className="hover:text-foreground">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
